import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	drawer: {
		width: "50vw",
		minWidth: 360,
		maxWidth: 650,
		flexShrink: 0,
	},
	drawerPaper: {
		width: "50vw",
		minWidth: 360,
		maxWidth: 650,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: "#eee",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		flexDirection: "column",
		gap: 8,
		padding: theme.spacing(2),
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	row: {
		padding: 10,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 8,
	},
	emptyState: {
		padding: theme.spacing(4),
		textAlign: "center",
		color: "#94a3b8",
	},
}));

const formatDateBR = dateStr => {
	if (!dateStr) return "";
	const [y, m, d] = dateStr.split("T")[0].split("-");
	return `${d}/${m}/${y}`;
};

const ReminderInstancesDrawer = ({ open, onClose, reminderType }) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [instances, setInstances] = useState([]);

	const fetchAllInstances = async () => {
		if (!reminderType) return;
		setLoading(true);
		try {
			const extracted = [];
			let pageNumber = 1;
			let hasMore = true;

			while (hasMore) {
				const { data } = await api.get("/contacts/", {
					params: { searchParam: "", pageNumber },
				});
				const contacts = data.contacts || [];

				contacts.forEach(contact => {
					const item = contact.extraInfo?.find(i => i.name === "LembretesCliente");
					if (!item || !item.value) return;
					try {
						const list = JSON.parse(item.value);
						if (!Array.isArray(list)) return;
						list.forEach(rem => {
							const matchesType =
								String(rem.typeId) === String(reminderType.id) ||
								rem.typeName === reminderType.name;
							if (matchesType) {
								extracted.push({
									...rem,
									contactId: contact.id,
									contactName: contact.name,
									contactNumber: contact.number,
									contactEmail: contact.email,
									contactExtraInfo: contact.extraInfo,
									whatsappName: contact.whatsapp?.name || "Sem conexão",
									whatsappColor: contact.whatsapp?.color || "#64748b",
								});
							}
						});
					} catch (e) {
						// ignora contato com extraInfo malformado
					}
				});

				hasMore = !!data.hasMore;
				pageNumber += 1;
			}

			extracted.sort((a, b) => new Date(a.date) - new Date(b.date));
			setInstances(extracted);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	};

	useEffect(() => {
		if (open) {
			fetchAllInstances();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, reminderType]);

	const handleDelete = async instance => {
		try {
			const currentInfo = [...(instance.contactExtraInfo || [])];
			const idx = currentInfo.findIndex(i => i.name === "LembretesCliente");
			if (idx < 0) return;

			let list = JSON.parse(currentInfo[idx].value);
			list = list.filter(r => r.id !== instance.id);
			currentInfo[idx].value = JSON.stringify(list);

			await api.put(`/contacts/${instance.contactId}`, {
				name: instance.contactName,
				number: instance.contactNumber,
				email: instance.contactEmail,
				extraInfo: currentInfo,
			});

			setInstances(prev => prev.filter(i => i.id !== instance.id));
			toast.success("Lembrete removido!");
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Drawer
			className={classes.drawer}
			anchor="right"
			open={open}
			onClose={onClose}
			classes={{ paper: classes.drawerPaper }}
		>
			<div className={classes.header} style={{ justifyContent: "space-between", paddingRight: 12 }}>
				<div style={{ display: "flex", alignItems: "center" }}>
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
					<span
						style={{
							display: "inline-block",
							width: 12,
							height: 12,
							borderRadius: "50%",
							backgroundColor: reminderType?.color || "#0284c7",
							marginRight: 8,
						}}
					/>
					<Typography style={{ fontWeight: "bold", fontSize: "0.95rem" }}>
						🔔 Lembretes: {reminderType?.name}
					</Typography>
				</div>
			</div>

			<div className={classes.content}>
				{loading ? (
					<div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
						<CircularProgress size={28} />
					</div>
				) : instances.length === 0 ? (
					<Typography className={classes.emptyState}>
						Nenhum lembrete cadastrado com este modelo.
					</Typography>
				) : (
					instances.map(instance => (
						<Paper key={instance.id} variant="outlined" className={classes.row}>
							<div style={{ flex: 1, minWidth: 0 }}>
								<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
									<Typography style={{ fontWeight: "bold", fontSize: "0.85rem" }}>
										{formatDateBR(instance.date)}
									</Typography>
									<Typography variant="body2" style={{ color: "#334155" }}>
										{instance.contactName}
									</Typography>
									<span
										style={{
											fontSize: "0.68rem",
											fontWeight: "bold",
											padding: "1px 6px",
											borderRadius: 4,
											backgroundColor: instance.status === "completed" ? "#dcfce7" : "#fef3c7",
											color: instance.status === "completed" ? "#166534" : "#b45309",
										}}
									>
										{instance.status === "completed" ? "Concluído" : "Pendente"}
									</span>
									<span
										style={{
											display: "inline-flex",
											alignItems: "center",
											gap: 4,
											fontSize: "0.68rem",
											fontWeight: "bold",
											padding: "1px 6px",
											borderRadius: 4,
											backgroundColor: "#f1f5f9",
											color: "#334155",
										}}
									>
										<span
											style={{
												display: "inline-block",
												width: 8,
												height: 8,
												borderRadius: "50%",
												backgroundColor: instance.whatsappColor,
											}}
										/>
										{instance.whatsappName}
									</span>
								</div>
								{instance.comment && (
									<Typography variant="caption" style={{ color: "#64748b" }}>
										{instance.comment}
									</Typography>
								)}
							</div>
							<IconButton size="small" onClick={() => handleDelete(instance)}>
								<DeleteOutlineIcon fontSize="small" />
							</IconButton>
						</Paper>
					))
				)}
			</div>
		</Drawer>
	);
};

export default ReminderInstancesDrawer;
